import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutGrid, Search, PlusCircle, Building2, Mail, Send, Users, Globe,
  SlidersHorizontal, RefreshCw, Database, X, ArrowUpRight, ArrowRight, Lock, FileText, Contact, Sparkles
} from "lucide-react";
import { dealsApi, subscribersApi, toolsApi, authApi } from "../api";
import { useAuth } from "../context/AuthContext";
import AddPropertyView from "./AddPropertyView";
import ContactsView from "./admin/ContactsView";
import TemplatesView from "./admin/TemplatesView";
import BlogView from "./admin/BlogView";
import DealSearch from "../components/DealSearch";
import { generateDealPdf } from "../utils/dealPdf";
import EmailDealModal from "../components/EmailDealModal";
import { toast } from "sonner";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return ""; } };

function AddDealModal({ onClose, onSaved }) {
  const [f, setF] = useState({ address: "", city: "", market: "Florida", rentalType: "Long-Term Rental", price: "", rent: "", payment: "", downPct: "20" });
  const save = async () => {
    if (!f.address || !f.price) { toast.error("Address and price are required"); return; }
    try {
      await dealsApi.create({ address: f.address, city: f.city, market: f.market, rentalType: f.rentalType, type: `Single Family · ${f.rentalType}`, price: Number(f.price), rent: Number(f.rent) || 0, payment: f.payment ? Number(f.payment) : null, downPct: f.downPct !== "" ? Number(f.downPct) : null, status: "LIVE" });
      toast.success("Deal published");
      onSaved();
      onClose();
    } catch (e) { toast.error("Could not save deal"); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "#111015", width: "100%", maxWidth: 460, padding: "2.4rem", position: "relative", borderColor: "rgba(201,162,75,0.2)" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 18, right: 18, background: "none", border: "none", color: "var(--muted)", cursor: "pointer" }}><X size={20} /></button>
        <h3 className="font-serif" style={{ fontSize: "1.6rem", marginBottom: "1.4rem" }}>Add New Deal</h3>
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <input className="field" placeholder="Street Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
          <input className="field" placeholder="City, State ZIP" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
          <select className="field" value={f.market} onChange={(e) => setF({ ...f, market: e.target.value })}>
            {["Florida", "Texas", "Georgia", "Carolinas", "Midwest", "Maryland", "Virginia", "DC / DMV", "Alabama", "Missouri", "Kansas", "Illinois", "Tennessee", "Michigan", "New York", "Pennsylvania"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <select className="field" data-testid="add-rental-type" value={f.rentalType} onChange={(e) => setF({ ...f, rentalType: e.target.value })}>
            {["Long-Term Rental", "Short-Term Rental"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.7rem" }}>
            <input className="field" type="number" placeholder="Price ($)" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
            <input className="field" type="number" placeholder={f.rentalType === "Short-Term Rental" ? "STR Revenue ($/mo)" : "Rent ($/mo)"} value={f.rent} onChange={(e) => setF({ ...f, rent: e.target.value })} />
          </div>
          <input className="field" type="number" placeholder="Est. Payment ($/mo) — optional" value={f.payment} onChange={(e) => setF({ ...f, payment: e.target.value })} />
          <input className="field" type="number" placeholder="Down Payment % (e.g. 25)" value={f.downPct} onChange={(e) => setF({ ...f, downPct: e.target.value })} />
          <button className="btn-gold" style={{ width: "100%" }} onClick={save}>Publish Deal</button>
        </div>
      </div>
    </div>
  );
}

function EditDealModal({ deal, onClose, onSaved }) {
  const [f, setF] = useState({
    address: deal.address || "", city: deal.city || "", market: deal.market || "Florida",
    rentalType: deal.rentalType || "Long-Term Rental",
    price: deal.price ?? "", rent: deal.rent ?? "", payment: deal.payment ?? "",
    downPct: deal.downPct ?? 20, status: deal.status || "LIVE",
  });
  const save = async () => {
    if (!f.address || !f.price) { toast.error("Address and price are required"); return; }
    try {
      await dealsApi.update(deal.id, {
        address: f.address, city: f.city, market: f.market, rentalType: f.rentalType,
        price: Number(f.price), rent: Number(f.rent) || 0,
        payment: f.payment !== "" ? Number(f.payment) : null,
        downPct: f.downPct !== "" ? Number(f.downPct) : null, status: f.status,
      });
      toast.success("Deal updated");
      onSaved(); onClose();
    } catch (e) { toast.error("Could not update deal"); }
  };
  return (
    <div onClick={onClose} data-testid="edit-deal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "#111015", width: "100%", maxWidth: 460, padding: "2.4rem", position: "relative", borderColor: "rgba(201,162,75,0.2)" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 18, right: 18, background: "none", border: "none", color: "var(--muted)", cursor: "pointer" }}><X size={20} /></button>
        <h3 className="font-serif" style={{ fontSize: "1.6rem", marginBottom: "1.4rem" }}>Edit Deal</h3>
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <input className="field" data-testid="edit-address" placeholder="Street Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
          <input className="field" data-testid="edit-city" placeholder="City, State ZIP" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
          <select className="field" data-testid="edit-market" value={f.market} onChange={(e) => setF({ ...f, market: e.target.value })}>
            {["Florida", "Texas", "Georgia", "Carolinas", "Midwest", "Maryland", "Virginia", "DC / DMV", "Alabama", "Missouri", "Kansas", "Illinois", "Tennessee", "Michigan", "New York", "Pennsylvania"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <select className="field" data-testid="edit-rental-type" value={f.rentalType} onChange={(e) => setF({ ...f, rentalType: e.target.value })}>
            {["Long-Term Rental", "Short-Term Rental"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.7rem" }}>
            <input className="field" data-testid="edit-price" type="number" placeholder="Price ($)" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
            <input className="field" data-testid="edit-rent" type="number" placeholder="Rent ($/mo)" value={f.rent} onChange={(e) => setF({ ...f, rent: e.target.value })} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.7rem" }}>
            <input className="field" data-testid="edit-payment" type="number" placeholder="Est. Payment ($/mo)" value={f.payment} onChange={(e) => setF({ ...f, payment: e.target.value })} />
            <input className="field" data-testid="edit-downpct" type="number" placeholder="Down Payment %" value={f.downPct} onChange={(e) => setF({ ...f, downPct: e.target.value })} />
          </div>
          <select className="field" data-testid="edit-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            {["LIVE", "DRAFT"].map((s) => <option key={s}>{s}</option>)}
          </select>
          <button className="btn-gold" data-testid="save-edit-deal" style={{ width: "100%" }} onClick={save}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

function AdminLogin({ onDone }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const submit = async () => {
    try {
      const u = await login(email, password);
      if (!u?.isAdmin) { toast.error("This account is not an admin"); return; }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid credentials"); }
  };
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#08080a" }}>
      <div className="card" style={{ padding: "2.8rem", width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: "1.6rem" }}>
          <div className="font-serif" style={{ fontSize: "1.6rem" }}><span style={{ color: "var(--text)" }}>DSCR</span><span style={{ color: "var(--gold)" }}>Daily</span></div>
          <span style={{ display: "inline-block", marginTop: 8, fontSize: "0.6rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--gold)", border: "1px solid var(--gold)", borderRadius: 999, padding: "0.2rem 0.8rem" }}>Admin Panel</span>
        </div>
        <p className="body-text" style={{ textAlign: "center", fontSize: "0.85rem", marginBottom: "1.6rem" }}><Lock size={13} style={{ verticalAlign: "middle" }} /> Sign in to manage deals & subscribers.</p>
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <input className="field" placeholder="Admin Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="field" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
          <button className="btn-gold" style={{ width: "100%" }} onClick={submit}>Sign In</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [view, setView] = useState("Dashboard");
  const [deals, setDeals] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ liveDeals: 0, drafts: 0, avgDscr: 0, avgCashflow: 0, subscribers: 0, recentActivity: [], integrations: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [emailDeal, setEmailDeal] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [ready, setReady] = useState(false);
  const [fltRental, setFltRental] = useState("all");
  const [fltMarket, setFltMarket] = useState("all");
  const [fltStatus, setFltStatus] = useState("all");
  const [fltSearch, setFltSearch] = useState("");

  const filteredDeals = useMemo(() => {
    const norm = (s) => (s || "").toLowerCase().replace(/,/g, " ").replace(/\s+/g, " ").trim();
    const q = norm(fltSearch);
    return deals.filter((d) => {
      if (fltRental !== "all" && (d.rentalType || "Long-Term Rental") !== fltRental) return false;
      if (fltMarket !== "all" && d.market !== fltMarket) return false;
      if (fltStatus !== "all" && d.status !== fltStatus) return false;
      if (q && !norm(`${d.address || ""} ${d.city || ""}`).includes(q)) return false;
      return true;
    });
  }, [deals, fltRental, fltMarket, fltStatus, fltSearch]);

  const isAdmin = user?.isAdmin;

  const refetch = async () => {
    try {
      const [d, s, st] = await Promise.all([
        dealsApi.list({ status: "all", sort: "dscr" }),
        subscribersApi.list(),
        toolsApi.stats(),
      ]);
      setDeals(d); setSubs(s); setStats(st);
      setLastSync(new Date());
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { if (isAdmin) refetch(); }, [isAdmin, ready]);

  if (loading) return <div style={{ minHeight: "100vh", background: "#08080a", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)" }}>Loading…</div>;
  if (!isAdmin) return <AdminLogin onDone={() => setReady((r) => !r)} />;

  const toggleStatus = async (id) => { await dealsApi.toggleStatus(id); refetch(); };
  const remove = async (id) => { await dealsApi.remove(id); toast("Deal deleted"); refetch(); };

  const nav = [
    ["MAIN", [["Dashboard", LayoutGrid, null], ["MLS Deal Finder", Search, "Auto"], ["Add Property", PlusCircle, null], ["All Properties", Building2, String(deals.length)]]],
    ["SUBSCRIBERS & CRM", [["Contacts", Contact, String(subs.length)], ["Email Templates", FileText, null], ["Weekly Update", Sparkles, "AI"], ["Send Digest", Send, null]]],
    ["PARTNERS", [["Partner Admin", Users, "ext"], ["Partner Portal", Globe, "ext"]]],
    ["SETTINGS", [["API Settings", SlidersHorizontal, null], ["Automation", RefreshCw, null], ["Database", Database, null]]],
  ];

  const StatCard = ({ label, value, sub, color }) => (
    <div className="card" style={{ padding: "1.5rem 1.6rem" }}>
      <div className="eyebrow" style={{ color: "var(--muted-2)", fontSize: "0.62rem" }}>{label}</div>
      <div className="font-serif" style={{ fontSize: "2.4rem", color: color || "var(--text)", lineHeight: 1.1, margin: "0.4rem 0 0.2rem" }}>{value}</div>
      <div style={{ fontSize: "0.72rem", color: "var(--muted-2)" }}>{sub}</div>
    </div>
  );

  return (
    <div className="admin-shell" style={{ display: "flex", minHeight: "100vh", background: "#08080a" }}>
      <aside className="admin-aside" style={{ width: 250, borderRight: "1px solid rgba(255,255,255,0.06)", padding: "1.8rem 1rem", position: "sticky", top: 0, height: "100vh", display: "flex", flexDirection: "column", overflowY: "auto" }}>
        <div style={{ padding: "0 0.6rem", marginBottom: "1.8rem" }}>
          <div className="font-serif" style={{ fontSize: "1.4rem" }}><span style={{ color: "var(--text)" }}>DSCR</span><span style={{ color: "var(--gold)" }}>Daily</span></div>
          <span style={{ display: "inline-block", marginTop: 6, fontSize: "0.6rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--gold)", border: "1px solid var(--gold)", borderRadius: 999, padding: "0.2rem 0.7rem" }}>Admin Panel</span>
        </div>
        <nav style={{ flex: 1 }}>
          {nav.map(([group, items]) => (
            <div key={group} style={{ marginBottom: "1.4rem" }}>
              <div style={{ fontSize: "0.6rem", letterSpacing: "0.18em", color: "var(--muted-2)", padding: "0 0.6rem", marginBottom: "0.5rem" }}>{group}</div>
              {items.map(([label, Icon, badge]) => (
                <button key={label} onClick={() => setView(label)} style={{ width: "100%", display: "flex", alignItems: "center", gap: "0.7rem", padding: "0.6rem", borderRadius: 4, border: "none", cursor: "pointer", textAlign: "left", fontSize: "0.82rem", marginBottom: 2, background: view === label ? "var(--gold-soft)" : "transparent", color: view === label ? "var(--gold)" : "var(--muted)", transition: "all 0.2s ease" }}>
                  <Icon size={16} /> <span style={{ flex: 1 }}>{label}</span>
                  {badge === "Auto" && <span style={{ fontSize: "0.58rem", padding: "0.1rem 0.45rem", borderRadius: 999, background: "rgba(123,196,127,0.15)", color: "var(--green)" }}>Auto</span>}
                  {badge === "ext" && <ArrowUpRight size={13} />}
                  {badge && badge !== "Auto" && badge !== "ext" && <span style={{ fontSize: "0.62rem", padding: "0.05rem 0.45rem", borderRadius: 999, background: "var(--gold-soft)", color: "var(--gold)" }}>{badge}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1rem", marginTop: "0.5rem" }}>
          {(stats.integrations || []).map((it) => (
            <div key={it.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.72rem", padding: "0.25rem 0.6rem" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--muted)" }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: it.status === "Connected" ? "var(--green)" : "var(--red)" }} /> {it.name}</span>
              <span style={{ color: it.status === "Connected" ? "var(--green)" : "var(--muted-2)" }}>{it.status}</span>
            </div>
          ))}
          <button onClick={() => navigate("/")} className="btn-dark" style={{ width: "100%", marginTop: "0.9rem", fontSize: "0.66rem" }}><ArrowUpRight size={13} /> View Live Site</button>
          <button onClick={() => { logout(); navigate("/"); }} style={{ width: "100%", marginTop: "0.5rem", background: "none", border: "none", color: "var(--muted-2)", fontSize: "0.7rem", cursor: "pointer", padding: "0.4rem" }}>Sign Out</button>
        </div>
      </aside>

      <main style={{ flex: 1, padding: "1.8rem 2.4rem" }}>
        <div className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
          <h1 className="font-serif" style={{ fontSize: "1.9rem" }}>{view}</h1>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span style={{ fontSize: "0.72rem", color: "var(--muted-2)" }}>{lastSync ? `Synced ${lastSync.toLocaleTimeString()}` : "Not synced yet"}</span>
            <button className="btn-dark" onClick={() => { refetch(); toast.success("Synced"); }}><RefreshCw size={13} /> Sync</button>
            <button className="btn-gold" onClick={() => setView("Add Property")}><PlusCircle size={14} /> Add Deal</button>
          </div>
        </div>

        {(view === "Dashboard" || view === "All Properties") && (
          <>
            {view === "Dashboard" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: "1.2rem", marginBottom: "1.6rem" }}>
                  <StatCard label="Live Deals" value={stats.liveDeals} sub="Published" />
                  <StatCard label="Drafts" value={stats.drafts} sub="Awaiting review" />
                  <StatCard label="Avg DSCR" value={stats.avgDscr} sub="Live properties" color="var(--green)" />
                  <StatCard label="Avg Cash Flow" value={(stats.avgCashflow >= 0 ? "+$" : "-$") + Math.abs(stats.avgCashflow)} sub="Est. monthly" color="var(--green)" />
                  <StatCard label="Subscribers" value={stats.subscribers} sub="Active alerts" />
                </div>

                <div style={{ marginBottom: "1.6rem" }}>
                  <div className="card" style={{ padding: "1.6rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
                      <span className="eyebrow" style={{ color: "var(--muted-2)" }}>Activity Log</span>
                      <span style={{ fontSize: "0.65rem", color: "var(--muted-2)" }}>{(stats.recentActivity || []).length} events</span>
                    </div>
                    <div style={{ display: "grid", gap: "0.6rem" }}>
                      {(stats.recentActivity || []).map((a, i) => (
                        <div key={i} style={{ display: "flex", gap: "0.8rem", alignItems: "center", fontSize: "0.78rem" }}>
                          <span style={{ color: "var(--muted-2)", minWidth: 76 }}>{fmtDate(a.time)}</span>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: a.ok ? "var(--green)" : "var(--gold)" }} />
                          <span style={{ color: "var(--text)" }}>{a.text}</span>
                        </div>
                      ))}
                      {(stats.recentActivity || []).length === 0 && <p className="body-text" style={{ fontSize: "0.76rem" }}>No recent activity yet.</p>}
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="card" style={{ padding: "1.6rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.2rem", flexWrap: "wrap", gap: "0.8rem" }}>
                <span className="eyebrow" style={{ color: "var(--muted-2)" }}>{view === "All Properties" ? "All Properties" : "Live Properties"}</span>
                <span style={{ fontSize: "0.7rem", color: "var(--muted-2)" }}>{filteredDeals.length} of {deals.length}</span>
              </div>
              <div className="admin-filter-bar" style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", marginBottom: "1.4rem" }}>
                <DealSearch deals={deals} value={fltSearch} onChange={setFltSearch} testId="admin-filter-search" placeholder="Search address / city…" style={{ flex: "1 1 220px", minWidth: 180 }} />
                <select className="field" data-testid="admin-filter-rental" value={fltRental} onChange={(e) => setFltRental(e.target.value)} style={{ width: "auto", fontSize: "0.8rem", padding: "0.5rem 0.8rem" }}>
                  <option value="all">All Strategies</option>
                  <option value="Long-Term Rental">Long-Term</option>
                  <option value="Short-Term Rental">Short-Term</option>
                </select>
                <select className="field" data-testid="admin-filter-market" value={fltMarket} onChange={(e) => setFltMarket(e.target.value)} style={{ width: "auto", fontSize: "0.8rem", padding: "0.5rem 0.8rem" }}>
                  <option value="all">All Markets</option>
                  {["Florida", "Texas", "Georgia", "Carolinas", "Midwest", "Maryland", "Virginia", "DC / DMV", "Alabama", "Missouri", "Kansas", "Illinois", "Tennessee", "Michigan", "New York", "Pennsylvania"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select className="field" data-testid="admin-filter-status" value={fltStatus} onChange={(e) => setFltStatus(e.target.value)} style={{ width: "auto", fontSize: "0.8rem", padding: "0.5rem 0.8rem" }}>
                  <option value="all">All Statuses</option>
                  <option value="LIVE">LIVE</option>
                  <option value="DRAFT">DRAFT</option>
                </select>
                {(fltRental !== "all" || fltMarket !== "all" || fltStatus !== "all" || fltSearch) && (
                  <button className="btn-dark" data-testid="admin-filter-clear" onClick={() => { setFltRental("all"); setFltMarket("all"); setFltStatus("all"); setFltSearch(""); }} style={{ fontSize: "0.72rem" }}>Clear</button>
                )}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                  <thead>
                    <tr style={{ textAlign: "left" }}>
                      {["Address", "Strategy", "Price", "Rent", "Payment", "Cash Flow", "DSCR", "Status", "Actions"].map((h) => (
                        <th key={h} style={{ fontSize: "0.62rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted-2)", fontWeight: 400, padding: "0 0.8rem 1rem" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeals.map((d) => (
                      <tr key={d.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                        <td style={{ padding: "1rem 0.8rem", fontSize: "0.85rem", color: "var(--text)" }}>{d.address}<span style={{ color: "var(--muted-2)" }}>{d.city ? `, ${d.city}` : ""}</span></td>
                        <td style={{ padding: "1rem 0.8rem", fontSize: "0.72rem", color: (d.rentalType || "Long-Term Rental") === "Short-Term Rental" ? "var(--gold)" : "var(--muted)" }}>{(d.rentalType || "Long-Term Rental") === "Short-Term Rental" ? "Short-Term" : "Long-Term"}</td>
                        <td style={{ padding: "1rem 0.8rem", fontSize: "0.85rem" }}>{money(d.price)}</td>
                        <td style={{ padding: "1rem 0.8rem", fontSize: "0.85rem", color: "var(--gold)" }}>${d.rent}/mo</td>
                        <td style={{ padding: "1rem 0.8rem", fontSize: "0.85rem" }}>${d.payment}/mo</td>
                        <td style={{ padding: "1rem 0.8rem", fontSize: "0.85rem", color: d.cashflow >= 0 ? "var(--green)" : "var(--red)" }}>{(d.cashflow >= 0 ? "+$" : "-$") + Math.abs(d.cashflow)}</td>
                        <td style={{ padding: "1rem 0.8rem", fontSize: "0.85rem", color: d.dscr >= 1 ? "var(--green)" : "var(--red)" }}>{d.dscr}</td>
                        <td style={{ padding: "1rem 0.8rem" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.68rem", color: d.status === "LIVE" ? "var(--green)" : "var(--muted)" }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: d.status === "LIVE" ? "var(--green)" : "var(--muted-2)" }} /> {d.status}</span></td>
                        <td style={{ padding: "1rem 0.8rem" }}>
                          <div style={{ display: "flex", gap: "0.4rem" }}>
                            <button onClick={() => setEditDeal(d)} data-testid={`edit-deal-${d.id}`} style={{ fontSize: "0.62rem", padding: "0.3rem 0.6rem", borderRadius: 3, border: "1px solid rgba(255,255,255,0.14)", background: "transparent", color: "var(--text)", cursor: "pointer", letterSpacing: "0.05em" }}>EDIT</button>
                            <button onClick={() => generateDealPdf(d)} data-testid={`admin-pdf-${d.id}`} title="Download deal sheet PDF" style={{ fontSize: "0.62rem", padding: "0.3rem 0.6rem", borderRadius: 3, border: "1px solid rgba(201,162,75,0.5)", background: "transparent", color: "var(--gold)", cursor: "pointer", letterSpacing: "0.05em" }}>PDF</button>
                            <button onClick={() => setEmailDeal(d)} data-testid={`admin-email-${d.id}`} title="Email this deal sheet to an investor" style={{ fontSize: "0.62rem", padding: "0.3rem 0.6rem", borderRadius: 3, border: "1px solid rgba(201,162,75,0.5)", background: "transparent", color: "var(--gold)", cursor: "pointer", letterSpacing: "0.05em" }}>EMAIL</button>
                            <button onClick={() => toggleStatus(d.id)} style={{ fontSize: "0.62rem", padding: "0.3rem 0.6rem", borderRadius: 3, border: "1px solid rgba(217,138,124,0.4)", background: "transparent", color: "var(--red)", cursor: "pointer", letterSpacing: "0.05em" }}>{d.status === "LIVE" ? "UNPUBLISH" : "PUBLISH"}</button>
                            <button onClick={() => remove(d.id)} style={{ fontSize: "0.7rem", padding: "0.3rem 0.5rem", borderRadius: 3, border: "1px solid rgba(217,138,124,0.4)", background: "transparent", color: "var(--red)", cursor: "pointer" }}><X size={12} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredDeals.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: "2rem 0.8rem", textAlign: "center", color: "var(--muted-2)" }}>{deals.length === 0 ? "No properties yet. Click “Add Deal”." : "No properties match these filters."}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {view === "Add Property" && (
          <AddPropertyView onSaved={refetch} goTo={(v) => setView(v)} />
        )}

        {view === "Contacts" && <ContactsView />}

        {view === "Email Templates" && <TemplatesView />}

        {view === "Weekly Update" && <BlogView />}

        {view === "Send Digest" && (
          <div className="card" style={{ padding: "2.4rem", maxWidth: 620 }}>
            <h3 className="font-serif" style={{ fontSize: "1.5rem", marginBottom: "0.6rem" }}>Send Deal Digest</h3>
            <p className="body-text" style={{ fontSize: "0.88rem", marginBottom: "1.6rem" }}>Send the latest live deals to all {subs.length} active subscribers via email, sent from <b>deals@dscrdaily.com</b>.</p>
            <div style={{ display: "flex", gap: "0.7rem", flexWrap: "wrap" }}>
              <button className="btn-gold" data-testid="send-digest-btn" onClick={async () => { try { const r = await toolsApi.sendDigest(); toast.success(`Digest: ${r.sent} sent, ${r.queued} queued to ${r.recipients} subscribers`); } catch { toast.error("Failed to send digest"); } }}><Send size={14} /> Send All Live Deals</button>
              <button className="btn-dark" data-testid="send-new-deals-btn" onClick={async () => { try { const r = await toolsApi.sendNewDeals("New DSCR Deals"); if (r.skipped) { toast("No new deals since last digest"); } else { toast.success(`New-deals digest: ${r.sent} sent to ${r.recipients} subscribers (${r.deals} deals)`); } } catch { toast.error("Failed"); } }}><Send size={14} /> Send New Deals Only</button>
            </div>
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: "1.8rem", paddingTop: "1.4rem" }}>
              <div className="eyebrow" style={{ color: "var(--muted-2)", marginBottom: "0.5rem" }}>Automated Schedule</div>
              <p className="body-text" style={{ fontSize: "0.82rem", marginBottom: "1rem" }}>New-deal digests auto-send <b>Monday 8AM</b> & a <b>Friday 8AM recap</b> (ET).</p>
            </div>
          </div>
        )}

        {!["Dashboard", "All Properties", "Contacts", "Email Templates", "Weekly Update", "Send Digest", "Add Property"].includes(view) && (
          <div className="card" style={{ padding: "3rem", textAlign: "center" }}>
            <p className="body-text">{view} — this module will be fully wired in a future phase.</p>
            <button className="btn-gold" style={{ marginTop: "1.2rem" }} onClick={() => setView("Dashboard")}>Back to Dashboard <ArrowRight size={14} /></button>
          </div>
        )}
      </main>
      {showAdd && <AddDealModal onClose={() => setShowAdd(false)} onSaved={refetch} />}
      {editDeal && <EditDealModal deal={editDeal} onClose={() => setEditDeal(null)} onSaved={refetch} />}
      {emailDeal && <EmailDealModal deal={emailDeal} onClose={() => setEmailDeal(null)} />}
    </div>
  );
}
